import { useEffect, useMemo, useRef, useState } from 'react';
import { useMatch, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, Button, Card, Spinner } from '@/components/ui';
import { customReportsApi } from '@/services/customReports.service';
import { isAxiosError } from 'axios';
import { extractFieldErrors, getErrorMessage } from '@/services/apiClient';
import { invalidateMyReports } from '@/hooks/useMyReports';
import { toast } from '@/store/toastStore';
import { AudienceBucketCheckbox, PersonMultiSelect } from '@/components/reports/AudienceControls';
import { HelpTip } from '@/components/reports/HelpTip';
import { FilterPanel } from '@/components/reports/ReportFilterPanel';
import type {
  CstOrder,
  LegacyIntColumn,
  ReportAudienceCatalog,
  ReportFamily,
  ReportFilterCatalog,
  ReportOptionSlot,
  ReportSubFilterCatalog,
} from '@/types/customReports.types';
import {
  CHART_OUTPUT_TYPES,
  DELIVER_EVERY_VALUES,
  HIDDEN_WIZARD_FAMILIES,
  RECURRING_VALUES,
  REPORT_INCREMENTS,
  REPORT_PROJECTIONS,
  REPORT_TIME_FRAMES,
  REPORT_TIME_FRAME_TODAY,
  SPREADSHEET_TYPE_SLUGS,
  adjustIncrementForTimeFrame,
  buildDisplayMetadata,
  buildSubFilterPayload,
  collapseCompositeSelections,
  decodeSubFilters,
  encodeAudienceBucket,
  filterCatalogSlots,
  formatFieldList,
  isRequiredChoiceSlot,
  normalizeStoredSlotValue,
  parseAudienceBucket,
  sanitizeEmailAddresses,
  spreadsheetTypeIdFromSlug,
  typesSelect1ForFamily,
  unsatisfiedSlots,
  type SubFilterSelections,
} from '@/utils/legacyOrderParity';
import { DatePicker } from '@/components/ui/DatePicker';

// ─── Wizard draft shape ──────────────────────────────────────────────────────

export interface WizardDraft {
  // Family + filters
  title: string;
  reportSelect: string;
  typesSelect1: string;
  typesSelect2: string;
  optionsSelect1: string;
  optionsSelect2: string;
  optionsSelect3: string;
  optionsSelect4: string;
  optionsSelect5: string;
  optionsSelect6: string;
  totalType: '1' | '2';
  subFilterSelections: SubFilterSelections;

  // Dates
  timeFrameStyle: '0' | '1' | '2';
  reportTimeFrame: string;
  reportDate1: string;
  reportDate2: string;
  reportIncrement: string;
  reportProjection: string;

  // Audience bucket flags (legacy 0/1) + share users (rcru)
  audienceCoordinators: boolean;
  audienceDoctors: boolean;
  audienceNurses: boolean;
  audienceUserlevel1: boolean;
  audienceUserlevel2: boolean;
  audienceUserlevel3: boolean;
  audienceUserlevel4: boolean;
  shareUserIds: string[];
  emailAddresses: string;

  // Delivery
  outputType: string;
  includeSpreadsheet: boolean;
  spreadsheetSlug: string;
  spreadsheetColumns: string;

  // Scheduling (CST only)
  recurring: '0' | '1';
  deliveryDate: string;
  deliveryTime: string;
  deliverEvery: string;
}

const EMPTY_DRAFT: WizardDraft = {
  title: '',
  reportSelect: '',
  typesSelect1: '',
  typesSelect2: '',
  optionsSelect1: '',
  optionsSelect2: '',
  optionsSelect3: '',
  optionsSelect4: '',
  optionsSelect5: '',
  optionsSelect6: '',
  totalType: '2',
  subFilterSelections: {},

  timeFrameStyle: '0',
  reportTimeFrame: REPORT_TIME_FRAME_TODAY,
  reportDate1: '',
  reportDate2: '',
  reportIncrement: '1',
  reportProjection: '1',

  audienceCoordinators: false,
  audienceDoctors: false,
  audienceNurses: false,
  audienceUserlevel1: false,
  audienceUserlevel2: false,
  audienceUserlevel3: false,
  audienceUserlevel4: false,
  shareUserIds: [],
  emailAddresses: '',

  outputType: '1',
  includeSpreadsheet: false,
  spreadsheetSlug: 'patients',
  spreadsheetColumns: '',

  recurring: '0',
  deliveryDate: '',
  deliveryTime: '00:00:00',
  deliverEvery: '1 day',
};

const STEPS = [
  { key: 'family',   label: '1. Family' },
  { key: 'filters',  label: '2. Filters' },
  { key: 'dates',    label: '3. Dates' },
  { key: 'audience', label: '4. Audience' },
  { key: 'delivery', label: '5. Delivery' },
  { key: 'review',   label: '6. Review & Run' },
] as const;

type StepKey = (typeof STEPS)[number]['key'];

/** Which of the three things a save can be, since each needs its own wording. */
export type ReportSaveMode = 'preset' | 'update' | 'recurring' | 'submit';

/**
 * What the user is told when a save succeeds.
 *
 * Submitting used to navigate away in silence: no confirmation, and a landing
 * list that could not yet contain the report, so a successful submission looked
 * exactly like a click that had done nothing.
 *
 * The wording promises no timing on purpose — nothing on this side knows when
 * the report will actually be produced, so "in a few minutes" would be a guess
 * — and it names no mechanism, because the reader is front-desk staff, not
 * whoever maintains the generator.
 */
export function reportSaveToast(mode: ReportSaveMode): {
  title: string;
  description?: string;
} {
  if (mode === 'preset') return { title: 'Preset saved' };
  if (mode === 'update') return { title: 'Changes saved' };

  if (mode === 'recurring') {
    return {
      title: 'Scheduled report saved',
      description: 'It will run on schedule and appear in My Reports each time.',
    };
  }

  return {
    title: 'Report submitted',
    description: "It's being prepared and will appear in My Reports when it's ready.",
  };
}

/**
 * Where each field the save endpoint can refuse lives in the wizard, named the way
 * the wizard labels it. No `label` means the step's choices in general — the
 * Filters dropdown labels change per report family, so naming the slot would
 * read as gibberish ("optionsSelect3").
 */
const SAVE_ERROR_FIELDS: Record<string, { step: StepKey; label?: string }> = {
  title: { step: 'family', label: 'Report title' },
  reportTitle: { step: 'family', label: 'Report title' },
  reportSelect: { step: 'family' },
  typesSelect1: { step: 'filters' },
  typesSelect2: { step: 'filters' },
  optionsSelect1: { step: 'filters' },
  optionsSelect2: { step: 'filters' },
  optionsSelect3: { step: 'filters' },
  optionsSelect4: { step: 'filters' },
  optionsSelect5: { step: 'filters' },
  optionsSelect6: { step: 'filters' },
  filterSelect: { step: 'filters' },
  subFilters: { step: 'filters' },
  subFiltersText: { step: 'filters' },
  filterText: { step: 'filters' },
  totalType: { step: 'filters', label: 'Totals' },
  timeFrameStyle: { step: 'dates', label: 'Date range' },
  reportTimeFrame: { step: 'dates', label: 'Date range' },
  reportDate1: { step: 'dates', label: 'Date range' },
  reportDate2: { step: 'dates', label: 'Date range' },
  reportIncrement: { step: 'dates', label: 'Increment' },
  reportProjection: { step: 'dates', label: 'Projection' },
  coordinators: { step: 'audience' },
  doctors: { step: 'audience' },
  nurses: { step: 'audience' },
  userlevel1: { step: 'audience' },
  userlevel2: { step: 'audience' },
  userlevel3: { step: 'audience' },
  userlevel4: { step: 'audience' },
  specificUsers: { step: 'audience' },
  shareUserIds: { step: 'audience', label: 'Share with specific users' },
  emailAddresses: { step: 'audience', label: 'Email recipients' },
  outputType: { step: 'delivery', label: 'Chart style' },
  action: { step: 'delivery', label: 'Chart style' },
  spreadsheetType: { step: 'delivery', label: 'Spreadsheet type' },
  spreadsheetColumns: { step: 'delivery', label: 'Spreadsheet type' },
  recurring: { step: 'delivery', label: 'Schedule' },
  deliveryDate: { step: 'delivery', label: 'First delivery' },
  deliverEvery: { step: 'delivery', label: 'Deliver every' },
};

const SAVE_FAILED = "This report wasn't saved.";

/**
 * What the user is told when a save is refused.
 *
 * The server's validation text used to go straight into the banner — "The report
 * projection field must be a string" — which names an internal field and a type,
 * and gives no way forward. This says which step to revisit, in the wizard's own
 * labels. The server text still goes to the console for whoever diagnoses it.
 */
export function describeSaveError(err: unknown): string {
  const fieldErrors = extractFieldErrors(err);
  const fields = Object.keys(fieldErrors);
  if (fields.length > 0) {
    console.error('[ReportBuilderWizard] save refused', fieldErrors);
  } else {
    console.error('[ReportBuilderWizard] save failed', err);
  }

  if (isAxiosError(err) && !err.response) {
    return `${SAVE_FAILED} Check your internet connection and submit again.`;
  }

  // Step -> labels to name there, or null to point at the step as a whole.
  const byStep = new Map<StepKey, Set<string> | null>();
  for (const field of fields) {
    // Array rules report per element: `shareUserIds.0`.
    const place = SAVE_ERROR_FIELDS[field.split('.')[0]];
    if (!place) continue;
    const labels = byStep.has(place.step) ? byStep.get(place.step)! : new Set<string>();
    if (labels === null || !place.label) {
      byStep.set(place.step, null);
    } else {
      byStep.set(place.step, labels.add(place.label));
    }
  }

  if (byStep.size === 0) {
    return fields.length > 0
      ? `${SAVE_FAILED} Check your choices on each step and submit again. If it keeps happening, contact Prospera support.`
      : `${SAVE_FAILED} Try again in a moment, or contact Prospera support if it keeps happening.`;
  }

  const places = STEPS.filter((s) => byStep.has(s.key)).map((s) => {
    const stepName = `the ${s.label.replace(/^\d+\.\s*/, '')} step`;
    const labels = byStep.get(s.key);
    return labels ? `${formatFieldList([...labels])} on ${stepName}` : `your choices on ${stepName}`;
  });
  return `${SAVE_FAILED} Please check ${formatFieldList(places)}, then submit again.`;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function ReportBuilderWizard() {
  // Routes that mount this component:
  //   /reports/builder/new              — blank draft
  //   /reports/orders/:orderId/edit     — edit a reports_cst_orders row
  //   /reports/presets/:presetId/edit   — edit the reports_cst_presets template in place
  //   /reports/presets/:presetId/use    — seed a NEW order from that template
  //
  // A preset carries the same column set as an order, so one hydrator serves all of
  // them; only the load/save endpoints and the return path differ.
  //
  // `use` exists because "Use as on-demand" used to POST a clone to
  // reports_cdm_orders and then send the user to /reports/builder/{cdmId} — an id this
  // wizard looked up in reports_cst_orders. It either 404'd, so the preset's settings
  // silently vanished, or it hydrated an unrelated CST order sharing that id and
  // overwrote it on save. Seeding the draft and creating nothing until Submit removes
  // both the id collision and the row nobody asked for.
  const { orderId, presetId } = useParams<{ orderId?: string; presetId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // Called unconditionally — hooks cannot sit behind a short-circuit.
  const usePresetRoute = useMatch('/reports/presets/:presetId/use');
  const isFromPreset = Boolean(presetId) && usePresetRoute !== null;
  const isPresetEdit = Boolean(presetId) && !isFromPreset;
  const numericPresetId = presetId ? Number(presetId) : null;
  const isEdit = isPresetEdit || Boolean(orderId && orderId !== 'new');
  const numericOrderId = !presetId && orderId && orderId !== 'new' ? Number(orderId) : null;
  const returnPath = isPresetEdit ? '/reports/presets' : '/reports/my-reports';

  const [step, setStep] = useState<StepKey>('family');
  const [draft, setDraft] = useState<WizardDraft>(EMPTY_DRAFT);
  const [saveError, setSaveError] = useState<string | null>(null);

  // ── Catalogs ─────────────────────────────────────────────────────────────
  const families = useQuery({
    queryKey: ['reports', 'catalog', 'families'],
    queryFn: customReportsApi.catalog.families,
    staleTime: 10 * 60_000,
  });

  const filterCatalog = useQuery<ReportFilterCatalog>({
    queryKey: ['reports', 'catalog', 'filters', draft.reportSelect],
    queryFn: () => customReportsApi.catalog.filters(draft.reportSelect),
    enabled: Boolean(draft.reportSelect),
    staleTime: 10 * 60_000,
  });

  const audienceCatalog = useQuery<ReportAudienceCatalog>({
    queryKey: ['reports', 'catalog', 'audience'],
    queryFn: customReportsApi.catalog.audience,
    staleTime: 5 * 60_000,
  });

  const subFilterCatalog = useQuery<ReportSubFilterCatalog>({
    queryKey: ['reports', 'catalog', 'sub-filters', draft.reportSelect],
    queryFn: () => customReportsApi.catalog.subFilters(draft.reportSelect),
    enabled: Boolean(draft.reportSelect),
    staleTime: 5 * 60_000,
  });

  // ── Hydrate draft when editing ───────────────────────────────────────────
  // Every wizard-saved order now lives in reports_cst_orders (the v4 cron's
  // polled queue), so edit mode always loads from the scheduled endpoint.
  const existingOrder = useQuery<CstOrder>({
    queryKey: ['reports', 'scheduled', numericOrderId],
    queryFn: () => customReportsApi.scheduled.get(numericOrderId as number),
    enabled: numericOrderId !== null,
  });

  // A preset stores the same definition shape, so the same hydrator applies.
  const existingPreset = useQuery({
    queryKey: ['reports', 'presets', numericPresetId],
    queryFn: () => customReportsApi.presets.get(numericPresetId as number),
    enabled: numericPresetId !== null,
  });

  // `?target=scheduled` (from the Presets page) preselects a recurring report so the
  // Delivery step opens on the schedule fields; `on-demand` leaves it one-off.
  const presetTarget = searchParams.get('target');

  useEffect(() => {
    const source = existingPreset.data ?? existingOrder.data;
    if (!source) return;
    const hydrated = hydrateDraftFromOrder(source as CstOrder);
    if (!isFromPreset) {
      setDraft(hydrated);
      return;
    }
    // Running a preset is a new order, so the template's own schedule state does not
    // carry over — the user picks the delivery date on the Delivery step.
    setDraft({
      ...hydrated,
      recurring: presetTarget === 'scheduled' ? '1' : '0',
      deliveryDate: '',
      deliveryTime: '00:00:00',
    });
  }, [existingOrder.data, existingPreset.data, isFromPreset, presetTarget]);

  // Second pass: collapse stored slot values the family's catalog cannot display.
  //
  // This has to be separate from hydration. The catalog query is keyed on
  // draft.reportSelect, which is only known once the row has been hydrated, so the
  // catalog cannot exist yet on the first pass. Legacy writes an unused select as '0'
  // and no metric/milestone/timeframe choice list has a '0' entry, so without this the
  // dropdown renders blank while validation saw a non-empty string.
  const normalizedFamilyRef = useRef<string | null>(null);
  useEffect(() => {
    const catalog = filterCatalog.data;
    if (!catalog || catalog.family !== draft.reportSelect) return;
    // Once per family, so a value the user deliberately clears is not re-normalized
    // out from under them on the next render.
    if (normalizedFamilyRef.current === catalog.family) return;
    normalizedFamilyRef.current = catalog.family;
    setDraft((d) => {
      const patch: Partial<WizardDraft> = {};
      for (const slot of filterCatalogSlots(catalog)) {
        const key = slot.slot as keyof WizardDraft;
        const current = (d[key] as string | undefined) ?? '';
        const next = normalizeStoredSlotValue(slot, current);
        if (next !== current) patch[key] = next as never;
      }
      return Object.keys(patch).length > 0 ? { ...d, ...patch } : d;
    });
  }, [filterCatalog.data, draft.reportSelect]);

  // A 404 or a network failure here used to fall through to EMPTY_DRAFT, so the user
  // saw a blank wizard and concluded their preset had not been carried over.
  const loadError = existingPreset.isError
    ? getErrorMessage(existingPreset.error, 'Could not load this preset')
    : existingOrder.isError
      ? getErrorMessage(existingOrder.error, 'Could not load this report')
      : null;

  const activeFamily = useMemo<ReportFamily | undefined>(
    () => families.data?.find((f) => f.key === draft.reportSelect),
    [families.data, draft.reportSelect],
  );

  const canAdvanceFromFamily = Boolean(draft.reportSelect);

  // Every family in ReportFamilyRegistry declares `subtype1 => typesSelect2`, so a
  // metric is always required. Checking it directly closes the hole where the
  // catalog-driven check below has nothing to check because the filter catalog hasn't
  // loaded (or failed): the legacy generator has no branch for typesSelect2='0' and
  // quietly falls back to a plain SUM, so the report computes "Amount" whatever was
  // intended.
  const hasMetric = draft.typesSelect2 !== '' && draft.typesSelect2 !== '0';

  // Which required dropdowns are still unanswered, by slot and by label. Continue used
  // to be gated on a bare boolean, so a user with every visible dropdown apparently set
  // got a dead button and no indication of which field was at fault — the one message
  // that named it rendered only on Review, which is unreachable while Continue is off.
  const missingSlots = useMemo(
    () => unsatisfiedSlots(filterCatalog.data, (key) => draft[key as keyof WizardDraft] as string),
    [filterCatalog.data, draft],
  );
  const missingSlotKeys = useMemo(
    () => new Set(missingSlots.map((slot) => slot.slot)),
    [missingSlots],
  );
  const missingFieldLabels = useMemo(() => {
    const labels = missingSlots.map((slot) => slot.label);
    // Before the catalog resolves there are no slots to inspect, but the metric is
    // required for every family — name it so the button is never dead-and-silent.
    const metricLabel = filterCatalog.data?.subtype1?.label ?? 'Metric';
    if (!hasMetric && !labels.includes(metricLabel)) labels.unshift(metricLabel);
    return labels;
  }, [missingSlots, filterCatalog.data, hasMetric]);
  const optionsValid = missingFieldLabels.length === 0;

  const hasAnySubFilter = Object.values(draft.subFilterSelections).some(
    (sel) => sel && sel.ids.filter((id) => id !== '' && id !== '0').length > 0,
  );
  // Legacy admin_report_list.php enforces at least one sub-filter when
  // Totals=Individual; v5 mirrors that to keep the chart from being empty.
  const canAdvanceFromFilters =
    optionsValid && (draft.totalType === '2' || hasAnySubFilter);
  const canAdvanceFromDates =
    draft.timeFrameStyle === '1'
      ? Boolean(draft.reportDate1 && draft.reportDate2)
      : Boolean(draft.reportTimeFrame);
  // A recurring order with no first-delivery date sends deliveryDate=undefined, and
  // ReportsScheduledController::store() only defaults it for NON-recurring orders —
  // so the column keeps its zero default, which satisfies the cron's
  // `deliveryDate <= NOW()` and the report fires on the next tick instead of the
  // date the user intended.
  const canAdvanceFromDelivery = draft.recurring !== '1' || Boolean(draft.deliveryDate);

  // Every step must hold before the order can be placed. Without this the Review
  // step's Submit was unguarded, so jumping straight here from step 1 persisted a
  // row with typesSelect2='0' — an aggregate selector the legacy generator has no
  // branch for, which silently falls through to a plain SUM.
  const blockers: string[] = [];
  if (!canAdvanceFromFamily) blockers.push('Pick a report on the Family step.');
  if (canAdvanceFromFamily && !optionsValid) {
    blockers.push(`Choose ${formatFieldList(missingFieldLabels)} on the Filters step.`);
  }
  if (canAdvanceFromFamily && optionsValid && draft.totalType !== '2' && !hasAnySubFilter) {
    blockers.push('Totals=Individual needs at least one sub-filter.');
  }
  if (!canAdvanceFromDates) blockers.push('Complete the date range on the Dates step.');
  if (!canAdvanceFromDelivery) {
    blockers.push('A recurring report needs a first delivery date.');
  }
  const canSubmit = blockers.length === 0;

  const stepIsSatisfied = (key: StepKey): boolean => {
    if (key === 'family') return canAdvanceFromFamily;
    if (key === 'filters') return canAdvanceFromFilters;
    if (key === 'dates') return canAdvanceFromDates;
    if (key === 'delivery') return canAdvanceFromDelivery;
    return true;
  };

  // ── Mutations ────────────────────────────────────────────────────────────
  // Single save path: persist the draft as a reports_cst_orders row. New
  // orders default to `deliveryDate = NOW()` server-side (see
  // ReportsScheduledController::store), so the v4 cron picks them up on its
  // next tick exactly like a legacy "Order" placed via admin_report_order3.php.
  // Edit-mode just updates the row in place (no re-queue) per spec.
  const placeOrder = useMutation({
    mutationFn: async (): Promise<unknown> => {
      const body = buildPayload(draft);
      // Preset mode edits the template in place. `shareUserIds` is an order-only
      // concept and is dropped server-side by the fillable intersection.
      // Preset-EDIT updates the template in place. Use-a-preset is a create: the
      // template must not be modified by running a report from it.
      if (isPresetEdit && numericPresetId !== null) {
        return customReportsApi.presets.update(numericPresetId, body);
      }
      return numericOrderId !== null
        ? customReportsApi.scheduled.update(numericOrderId, body)
        : customReportsApi.scheduled.create(body);
    },
    onSuccess: () => {
      setSaveError(null);
      // Every My Reports source, not the two hand-picked keys this used to
      // name — `reports/onDemand` matched no query in the app, and the results
      // query the custom rows are actually built from was never touched.
      invalidateMyReports(qc);
      void qc.invalidateQueries({ queryKey: ['reports', 'presets'] });
      // Say something: this navigates away, and the report it just created will
      // not exist for some minutes yet.
      const saved = reportSaveToast(
        isPresetEdit && numericPresetId !== null
          ? 'preset'
          : numericOrderId !== null
            ? 'update'
            : draft.recurring === '1'
              ? 'recurring'
              : 'submit',
      );
      toast.success(saved.title, saved.description);
      navigate(returnPath);
    },
    onError: (err) => setSaveError(describeSaveError(err)),
  });

  const isSaving = placeOrder.isPending;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="p-6">
      <PageHeader
        variant="compact"
        title={isPresetEdit ? 'Edit Preset' : isEdit ? 'Edit Report' : 'New Report'}
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(presetId ? '/reports/presets' : '/reports')}
          >
            Cancel
          </Button>
        }
      />

      <StepNav current={step} onJump={setStep} isSatisfied={stepIsSatisfied} />

      {loadError && (
        <Alert variant="error" className="mt-4">
          {loadError}
        </Alert>
      )}

      <Card className="mt-4" shadow="sm">
        {step === 'family' && (
          <StepFamily
            title={draft.title}
            onTitleChange={(title) => setDraft((d) => ({ ...d, title }))}
            families={families.data ?? []}
            isLoading={families.isLoading}
            value={draft.reportSelect}
            onChange={(family) =>
              setDraft((d) => ({
                ...EMPTY_DRAFT,
                title: d.title.trim() ? d.title : family.label,
                reportSelect: family.key,
                typesSelect1: typesSelect1ForFamily(family.key),
                timeFrameStyle: d.timeFrameStyle,
                reportTimeFrame: d.reportTimeFrame,
                reportDate1: d.reportDate1,
                reportDate2: d.reportDate2,
                reportIncrement: d.reportIncrement,
                reportProjection: d.reportProjection,
                audienceCoordinators: d.audienceCoordinators,
                audienceDoctors: d.audienceDoctors,
                audienceNurses: d.audienceNurses,
                audienceUserlevel1: d.audienceUserlevel1,
                audienceUserlevel2: d.audienceUserlevel2,
                audienceUserlevel3: d.audienceUserlevel3,
                audienceUserlevel4: d.audienceUserlevel4,
                shareUserIds: d.shareUserIds,
                emailAddresses: d.emailAddresses,
                outputType: d.outputType,
                includeSpreadsheet: d.includeSpreadsheet,
                spreadsheetSlug: d.spreadsheetSlug,
                spreadsheetColumns: d.spreadsheetColumns,
                recurring: d.recurring,
                deliveryDate: d.deliveryDate,
                deliveryTime: d.deliveryTime,
                deliverEvery: d.deliverEvery,
              }))
            }
          />
        )}

        {step === 'filters' && (
          <StepFilters
            family={activeFamily}
            catalog={filterCatalog.data}
            subFilterCatalog={subFilterCatalog.data}
            isLoading={filterCatalog.isLoading || subFilterCatalog.isLoading}
            catalogError={
              filterCatalog.isError
                ? getErrorMessage(filterCatalog.error, 'Could not load the filter catalog')
                : subFilterCatalog.isError
                  ? getErrorMessage(subFilterCatalog.error, 'Could not load the filter lists')
                  : null
            }
            missingFieldLabels={missingFieldLabels}
            missingSlotKeys={missingSlotKeys}
            draft={draft}
            onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
          />
        )}

        {step === 'dates' && (
          <StepDates draft={draft} onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))} />
        )}

        {step === 'audience' && (
          <StepAudience
            draft={draft}
            catalog={audienceCatalog.data}
            isLoading={audienceCatalog.isLoading}
            onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
          />
        )}

        {step === 'delivery' && (
          <StepDelivery
            draft={draft}
            onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
          />
        )}

        {step === 'review' && (
          <StepReview
            draft={draft}
            saveError={saveError}
            catalog={filterCatalog.data}
            blockers={blockers}
          />
        )}
      </Card>

      <div className="mt-6 flex items-center justify-between">
        <Button
          variant="secondary"
          onClick={() => {
            const i = STEPS.findIndex((s) => s.key === step);
            if (i > 0) setStep(STEPS[i - 1].key);
          }}
          disabled={step === 'family' || isSaving}
        >
          Back
        </Button>

        {step === 'review' ? (
          <Button
            onClick={() => placeOrder.mutate()}
            loading={isSaving}
            disabled={!canSubmit}
          >
            {isEdit
              ? 'Save changes'
              : draft.recurring === '1'
                ? 'Save as scheduled report'
                : 'Submit'}
          </Button>
        ) : (
          <Button
            onClick={() => {
              const i = STEPS.findIndex((s) => s.key === step);
              if (i < STEPS.length - 1) setStep(STEPS[i + 1].key);
            }}
            disabled={
              (step === 'family' && !canAdvanceFromFamily) ||
              (step === 'filters' && !canAdvanceFromFilters) ||
              (step === 'dates' && !canAdvanceFromDates) ||
              (step === 'delivery' && !canAdvanceFromDelivery)
            }
          >
            Continue
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Step navigation tabs ────────────────────────────────────────────────────

function StepNav({
  current,
  onJump,
  isSatisfied,
}: {
  current: StepKey;
  onJump: (k: StepKey) => void;
  /** Whether a step's own requirements are met, so forward jumps can't skip it. */
  isSatisfied: (k: StepKey) => boolean;
}) {
  const currentIndex = STEPS.findIndex((s) => s.key === current);

  return (
    <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-3">
      {STEPS.map((s, i) => {
        const isActive = s.key === current;
        // Going back is always allowed. Going forward requires every step in
        // between to be satisfied — otherwise the user could jump to Review and
        // submit an order with no metric selected.
        const blocked =
          i > currentIndex &&
          STEPS.slice(0, i).some((prior) => !isSatisfied(prior.key));

        return (
          <button
            key={s.key}
            type="button"
            onClick={() => onJump(s.key)}
            disabled={blocked}
            className={
              'rounded-md px-3 py-1.5 text-sm font-medium transition ' +
              (isActive
                ? 'bg-slate-900 text-white'
                : blocked
                  ? 'cursor-not-allowed bg-slate-50 text-slate-400'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200')
            }
          >
            {s.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Step: Family ────────────────────────────────────────────────────────────

function StepFamily({
  title,
  onTitleChange,
  families,
  isLoading,
  value,
  onChange,
}: {
  title: string;
  onTitleChange: (v: string) => void;
  families: ReportFamily[];
  isLoading: boolean;
  value: string;
  onChange: (family: ReportFamily) => void;
}) {
  return (
    <div className="space-y-5">
      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">Report title</label>
        <input
          type="text"
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-900 focus:outline-none"
          placeholder="e.g. Monthly Appointments by Coordinator"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
        />
      </div>

      <div>
        <h2 className="mb-3 text-lg font-semibold text-slate-900">Pick a report</h2>
        {isLoading ? (
          <div className="flex items-center gap-2 text-slate-500">
            <Spinner size="sm" /> Loading report families…
          </div>
        ) : families.length === 0 ? (
          <div className="text-slate-500">No report families configured.</div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {families
              .filter((f) => !HIDDEN_WIZARD_FAMILIES.has(f.key))
              .map((family) => {
                const isSelected = value === family.key;
                const displayLabel = family.key === 'AllPayments' ? 'Payments' : family.label;
                return (
                  <button
                    key={family.key}
                    type="button"
                    onClick={() => onChange(family)}
                    className={
                      'rounded-md border p-4 text-left transition ' +
                      (isSelected
                        ? 'border-slate-900 bg-slate-50 ring-2 ring-slate-900'
                        : 'border-slate-200 bg-white hover:border-slate-400')
                    }
                  >
                    <div className="text-sm font-semibold text-slate-900">{displayLabel}</div>
                  </button>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Step: Filters ───────────────────────────────────────────────────────────

function StepFilters({
  family,
  catalog,
  subFilterCatalog,
  isLoading,
  catalogError,
  missingFieldLabels,
  missingSlotKeys,
  draft,
  onChange,
}: {
  family: ReportFamily | undefined;
  catalog: ReportFilterCatalog | undefined;
  subFilterCatalog: ReportSubFilterCatalog | undefined;
  isLoading: boolean;
  /** Set when a catalog query failed — previously indistinguishable from loading. */
  catalogError: string | null;
  /** Labels of the required dropdowns still unanswered, for the step's own prompt. */
  missingFieldLabels: string[];
  /** Slot keys in that list, so each control can mark itself. */
  missingSlotKeys: Set<string>;
  draft: WizardDraft;
  onChange: (patch: Partial<WizardDraft>) => void;
}) {
  if (!family) {
    return <div className="text-slate-500">Pick a family in step 1 first.</div>;
  }
  // Order matters: a failed query leaves `catalog` undefined too, and reporting it as a
  // permanent "Loading…" spinner was how a broken catalog looked like a hung page.
  if (catalogError) {
    return <Alert variant="error">{catalogError}</Alert>;
  }
  if (isLoading || !catalog) {
    return (
      <div className="flex items-center gap-2 text-slate-500">
        <Spinner size="sm" /> Loading filter catalog…
      </div>
    );
  }

  const subtype1 = visibleSlot(catalog.subtype1);
  const subtype2 = visibleSlot(catalog.subtype2);
  const options = catalog.options.filter(visibleSlot);
  const hasVisibleFields = Boolean(subtype1 || subtype2 || options.length > 0);

  const setSelections = (next: SubFilterSelections) => onChange({ subFilterSelections: next });

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-slate-900">
        Configure <span className="text-slate-500">{family.label}</span>
      </h2>

      {subtype1 && (
        <FilterSelect
          slot={subtype1}
          value={slotValue(draft, subtype1)}
          onChange={(v) => onChange(slotPatch(subtype1, v))}
          invalid={missingSlotKeys.has(subtype1.slot)}
        />
      )}
      {subtype2 && (
        <FilterSelect
          slot={subtype2}
          value={slotValue(draft, subtype2)}
          onChange={(v) => onChange(slotPatch(subtype2, v))}
          invalid={missingSlotKeys.has(subtype2.slot)}
        />
      )}

      {options.map((slot, idx) => {
        const slotKey = slot.slot as keyof WizardDraft;
        return (
          <FilterSelect
            key={slot.slot}
            slot={slot}
            value={(draft[slotKey] as string) ?? ''}
            onChange={(v) => onChange({ [slotKey]: v } as Partial<WizardDraft>)}
            autoFocus={idx === 0 && !subtype1 && !subtype2}
            invalid={missingSlotKeys.has(slot.slot)}
          />
        );
      })}

      {!hasVisibleFields && (
        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
          This family has no additional options.
        </div>
      )}

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">
          Totals
          <HelpTip text="Overall combines every record into one total. Individual breaks the chart into one row per sub-filter you pick below — set Apply to All to keep a sub-filter scoping the dataset without giving it its own row." />
        </label>
        <select
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
          value={draft.totalType}
          onChange={(e) => onChange({ totalType: e.target.value as WizardDraft['totalType'] })}
        >
          <option value="2">Overall</option>
          <option value="1">Individual</option>
        </select>
      </div>

      <FilterPanel
        catalog={subFilterCatalog}
        selections={draft.subFilterSelections}
        onChange={setSelections}
        showApplyToAll={draft.totalType === '1'}
      />

      {draft.totalType === '1' && !someSelectionMade(draft.subFilterSelections) && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Pick at least one sub-filter below — Individual reports build their chart rows from
          your selections.
        </div>
      )}

      {/* Continue is disabled until every required dropdown above is answered. Saying
          which ones is the whole point: the fields sit above the Filters panel, so a
          user scrolled down to the checkboxes had no way to see what was missing. */}
      {missingFieldLabels.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Choose {formatFieldList(missingFieldLabels)} above to continue.
        </div>
      )}
    </div>
  );
}

function someSelectionMade(selections: SubFilterSelections): boolean {
  return Object.values(selections).some(
    (sel) => sel && sel.ids.filter((id) => id !== '' && id !== '0').length > 0,
  );
}

function visibleSlot(slot: ReportOptionSlot | null | undefined): ReportOptionSlot | null {
  if (!slot) return null;
  if (slot.type === 'select' && slot.choices.length === 0) return null;
  return slot;
}

function slotValue(draft: WizardDraft, slot: ReportOptionSlot | null | undefined): string {
  if (!slot) return '';
  return (draft[slot.slot as keyof WizardDraft] as string | undefined) ?? '';
}

function slotPatch(slot: ReportOptionSlot, value: string): Partial<WizardDraft> {
  return { [slot.slot]: value } as Partial<WizardDraft>;
}

function FilterSelect({
  slot,
  value,
  onChange,
  autoFocus,
  invalid,
}: {
  slot: ReportOptionSlot;
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
  /** Required and unanswered — every select slot the catalog defines is mandatory. */
  invalid?: boolean;
}) {
  const hasChoices = slot.choices.length > 0;
  const required = isRequiredChoiceSlot(slot);
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">
        {slot.label}
        {required && <span className="ml-0.5 text-rose-600">*</span>}
      </label>
      {hasChoices ? (
        <select
          className={`w-full rounded-md border bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none ${
            invalid
              ? 'border-rose-400 focus:border-rose-500'
              : 'border-slate-300 focus:border-slate-900'
          }`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoFocus={autoFocus}
          aria-invalid={invalid || undefined}
        >
          <option value="">Select…</option>
          {slot.choices.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type="text"
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-900 focus:outline-none"
          placeholder="Enter a value"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

// ─── Step: Dates ─────────────────────────────────────────────────────────────

function StepDates({
  draft,
  onChange,
}: {
  draft: WizardDraft;
  onChange: (patch: Partial<WizardDraft>) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Date range</h2>
      </div>

      <div className="space-y-3">
        <label className="flex cursor-pointer items-start gap-3 rounded-md border border-slate-200 p-3 hover:border-slate-400">
          <input
            type="radio"
            name="timeFrameStyle"
            className="mt-1"
            checked={draft.timeFrameStyle === '0'}
            onChange={() => onChange({ timeFrameStyle: '0' })}
          />
          <div className="flex-1">
            <div className="text-sm font-medium text-slate-900">
              Rolling window
              <HelpTip text="The date range updates automatically each time the report runs. For example, 'Last 30 days' always looks at the most recent 30 days." />
            </div>
            {draft.timeFrameStyle === '0' && (
              <div className="mt-3">
                <select
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
                  value={draft.reportTimeFrame}
                  onChange={(e) =>
                    onChange({
                      reportTimeFrame: e.target.value,
                      // Legacy adjustIncrement() (admin_report_list.php:3839-3856)
                      // runs off this same onchange, so e.g. Year-to-date upgrades
                      // Daily to Monthly. Without it an identical selection yields
                      // ~365 daily buckets here vs 12 monthly ones in FTP.
                      reportIncrement: adjustIncrementForTimeFrame(
                        e.target.value,
                        draft.reportIncrement,
                      ),
                    })
                  }
                >
                  {REPORT_TIME_FRAMES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </label>

        <label className="flex cursor-pointer items-start gap-3 rounded-md border border-slate-200 p-3 hover:border-slate-400">
          <input
            type="radio"
            name="timeFrameStyle"
            className="mt-1"
            checked={draft.timeFrameStyle === '1'}
            onChange={() => onChange({ timeFrameStyle: '1' })}
          />
          <div className="flex-1">
            <div className="text-sm font-medium text-slate-900">
              Fixed range
              <HelpTip text="Pick exact start and end dates. The report will always cover that same time period no matter when you run it." />
            </div>
            {draft.timeFrameStyle === '1' && (
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="report-fixed-start" className="mb-1 block text-xs font-medium text-slate-600">Start</label>
                  <DatePicker
                    id="report-fixed-start"
                    value={draft.reportDate1}
                    onChange={(value) => onChange({ reportDate1: value })}
                  />
                </div>
                <div>
                  <label htmlFor="report-fixed-end" className="mb-1 block text-xs font-medium text-slate-600">End</label>
                  <DatePicker
                    id="report-fixed-end"
                    value={draft.reportDate2}
                    min={draft.reportDate1 || undefined}
                    onChange={(value) => onChange({ reportDate2: value })}
                  />
                </div>
              </div>
            )}
          </div>
        </label>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">
            Increment
            <HelpTip text="How the report groups your data over time. 'Daily' shows one column per day, 'Monthly' shows one column per month, etc." />
          </label>
          <select
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
            value={draft.reportIncrement}
            onChange={(e) => onChange({ reportIncrement: e.target.value })}
          >
            {REPORT_INCREMENTS.map((i) => (
              <option key={i.value} value={i.value}>
                {i.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">
            Projection
            <HelpTip text="Optionally adds predicted future data points to your chart based on recent trends. Leave it Off if you only want actual data." />
          </label>
          <select
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
            value={draft.reportProjection}
            onChange={(e) => onChange({ reportProjection: e.target.value })}
          >
            {REPORT_PROJECTIONS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

// ─── Step: Audience ──────────────────────────────────────────────────────────

function StepAudience({
  draft,
  catalog,
  isLoading,
  onChange,
}: {
  draft: WizardDraft;
  catalog: ReportAudienceCatalog | undefined;
  isLoading: boolean;
  onChange: (patch: Partial<WizardDraft>) => void;
}) {
  if (isLoading || !catalog) {
    return (
      <div className="flex items-center gap-2 text-slate-500">
        <Spinner size="sm" /> Loading audience catalog…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">
          Audience
          <HelpTip text="Choose which staff roles can also see this report on the dashboard. If no roles are checked, only you (the order owner) and any users you explicitly share with below can see it." />
        </h2>
      </div>

      <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
        <div className="mb-2 text-sm font-medium text-slate-900">User roles</div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <AudienceBucketCheckbox
            label="Coordinators"
            checked={draft.audienceCoordinators}
            onChange={(v) => onChange({ audienceCoordinators: v })}
          />
          <AudienceBucketCheckbox
            label="Doctors"
            checked={draft.audienceDoctors}
            onChange={(v) => onChange({ audienceDoctors: v })}
          />
          <AudienceBucketCheckbox
            label="Nurses"
            checked={draft.audienceNurses}
            onChange={(v) => onChange({ audienceNurses: v })}
          />
          <AudienceBucketCheckbox
            label="User level 1"
            checked={draft.audienceUserlevel1}
            onChange={(v) => onChange({ audienceUserlevel1: v })}
          />
          <AudienceBucketCheckbox
            label="User level 2"
            checked={draft.audienceUserlevel2}
            onChange={(v) => onChange({ audienceUserlevel2: v })}
          />
          <AudienceBucketCheckbox
            label="User level 3"
            checked={draft.audienceUserlevel3}
            onChange={(v) => onChange({ audienceUserlevel3: v })}
          />
          <AudienceBucketCheckbox
            label="User level 4"
            checked={draft.audienceUserlevel4}
            onChange={(v) => onChange({ audienceUserlevel4: v })}
          />
        </div>
      </div>

      <PersonMultiSelect
        label="Share with specific users"
        people={catalog.users}
        value={draft.shareUserIds}
        onChange={(v) => onChange({ shareUserIds: v })}
      />

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">
          Email recipients
          <HelpTip text="Optional. When a spreadsheet is included, these addresses receive the file by email." />
        </label>
        <textarea
          rows={2}
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
          placeholder="e.g. manager@clinic.com, frontdesk@clinic.com"
          value={draft.emailAddresses}
          onChange={(e) => onChange({ emailAddresses: e.target.value })}
        />
      </div>
    </div>
  );
}

// AudienceBucketCheckbox + PersonMultiSelect now live in
// components/reports/AudienceControls.tsx (shared with the AI report share modal).

// ─── Step: Delivery ──────────────────────────────────────────────────────────

function StepDelivery({
  draft,
  onChange,
}: {
  draft: WizardDraft;
  onChange: (patch: Partial<WizardDraft>) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Delivery</h2>
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-slate-700">
          Chart style
          <HelpTip text="How the report chart appears when results are generated — table, line, bar, etc." />
        </label>
        <select
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
          // A legacy order can hold a delivery token ('das', 'pbi', …) that isn't a
          // chart style. Show Table for those rather than an empty select, but leave
          // draft.outputType alone so saving without touching this keeps the original.
          value={
            CHART_OUTPUT_TYPES.some((o) => o.value === draft.outputType)
              ? draft.outputType
              : '1'
          }
          onChange={(e) => onChange({ outputType: e.target.value })}
        >
          {CHART_OUTPUT_TYPES.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">
          Include spreadsheet
          <HelpTip text="When Yes, a detail spreadsheet is generated alongside the chart (same as legacy Include Spreadsheet)." />
        </label>
        <select
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
          value={draft.includeSpreadsheet ? '1' : '2'}
          onChange={(e) => onChange({ includeSpreadsheet: e.target.value === '1' })}
        >
          <option value="2">No</option>
          <option value="1">Yes</option>
        </select>
      </div>

      {draft.includeSpreadsheet && (
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Spreadsheet type</label>
          <select
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
            value={draft.spreadsheetSlug}
            onChange={(e) => onChange({ spreadsheetSlug: e.target.value })}
          >
            {SPREADSHEET_TYPE_SLUGS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
        <h3 className="text-sm font-semibold text-slate-900">
          Schedule
          <HelpTip text="One time runs the report once. Recurring repeats it on the interval you pick." />
        </h3>
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Occurrence</label>
            <select
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
              value={draft.recurring}
              onChange={(e) => onChange({ recurring: e.target.value as WizardDraft['recurring'] })}
            >
              {RECURRING_VALUES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          {draft.recurring === '1' && (
            <>
              <div>
                <label htmlFor="report-first-delivery" className="mb-1 block text-xs font-medium text-slate-600">First delivery</label>
                <DatePicker
                  id="report-first-delivery"
                  value={draft.deliveryDate}
                  onChange={(value) => onChange({ deliveryDate: value })}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Delivery time</label>
                <input
                  type="time"
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
                  value={draft.deliveryTime.slice(0, 5)}
                  onChange={(e) => onChange({ deliveryTime: `${e.target.value}:00` })}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Deliver every</label>
                <select
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
                  value={draft.deliverEvery}
                  onChange={(e) => onChange({ deliverEvery: e.target.value })}
                >
                  {DELIVER_EVERY_VALUES.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Step: Review ────────────────────────────────────────────────────────────

function StepReview({
  draft,
  saveError,
  catalog,
  blockers = [],
}: {
  draft: WizardDraft;
  saveError: string | null;
  catalog?: ReportFilterCatalog;
  /** Unmet requirements from earlier steps; Submit stays disabled while non-empty. */
  blockers?: string[];
}) {
  const summary = buildSummary(draft, catalog);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Review</h2>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {summary.map((row) => (
          <div key={row.label} className="rounded-md border border-slate-200 bg-white p-3">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
              {row.label}
            </div>
            <div className="mt-1 text-sm text-slate-900">{row.value || '—'}</div>
          </div>
        ))}
      </div>

      {blockers.length > 0 && (
        <Alert variant="error">
          <ul className="list-inside list-disc space-y-1">
            {blockers.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </Alert>
      )}

      {saveError && <Alert variant="error">{saveError}</Alert>}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function buildPayload(d: WizardDraft): Partial<CstOrder> & { shareUserIds?: number[] } {
  const subFilterPayload = buildSubFilterPayload(d.totalType, d.subFilterSelections);
  const metadata = buildDisplayMetadata(
    d.reportSelect,
    d.title.trim() || d.reportSelect,
    d.typesSelect2 || '0',
  );
  const spreadsheetType = d.includeSpreadsheet ? spreadsheetTypeIdFromSlug(d.spreadsheetSlug) : '0';

  const payload: Partial<CstOrder> & { shareUserIds?: number[] } = {
    title: d.title.trim() || d.reportSelect,
    reportSelect: d.reportSelect,

    typesSelect1: typesSelect1ForFamily(d.reportSelect),
    typesSelect2: d.typesSelect2 || '0',
    optionsSelect1: d.optionsSelect1 || '0',
    optionsSelect2: d.optionsSelect2 || '0',
    optionsSelect3: d.optionsSelect3 || '0',
    optionsSelect4: d.optionsSelect4 || '0',
    optionsSelect5: d.optionsSelect5 || '0',
    optionsSelect6: d.optionsSelect6 || '0',

    filterSelect: '0',
    totalType: d.totalType,
    subFilters: subFilterPayload.subFilters,
    subFiltersText: subFilterPayload.subFiltersText,
    filterText: subFilterPayload.filterText,

    reportTitle: metadata.reportTitle,
    reportSubTitle: metadata.reportSubTitle,
    reportOptionsTitle: metadata.reportOptionsTitle,
    colorArray: metadata.colorArray,

    timeFrameStyle: d.timeFrameStyle,
    reportTimeFrame: d.timeFrameStyle === '0' ? d.reportTimeFrame : undefined,
    reportDate1: d.timeFrameStyle === '1' ? d.reportDate1 : undefined,
    reportDate2: d.timeFrameStyle === '1' ? d.reportDate2 : undefined,
    reportIncrement: d.reportIncrement,
    reportProjection: d.reportProjection,

    coordinators: encodeAudienceBucket(d.audienceCoordinators),
    doctors: encodeAudienceBucket(d.audienceDoctors),
    nurses: encodeAudienceBucket(d.audienceNurses),
    userlevel1: encodeAudienceBucket(d.audienceUserlevel1),
    userlevel2: encodeAudienceBucket(d.audienceUserlevel2),
    userlevel3: encodeAudienceBucket(d.audienceUserlevel3),
    userlevel4: encodeAudienceBucket(d.audienceUserlevel4),
    specificUsers: '0',

    emailAddresses: sanitizeEmailAddresses(d.emailAddresses.trim()),

    outputType: d.outputType || '1',
    action: d.outputType || '1',
    generateType: '0',
    spreadsheetType,
    spreadsheetColumns: d.spreadsheetColumns || '',

    recurring: d.recurring,
    shareUserIds: d.shareUserIds.map((id) => Number(id)).filter((id) => id > 0),
  };

  if (d.recurring === '1') {
    payload.deliveryDate =
      d.deliveryDate && d.deliveryTime
        ? `${d.deliveryDate} ${d.deliveryTime}`
        : d.deliveryDate || undefined;
    payload.deliverEvery = d.deliverEvery || '1 day';
  }

  return payload;
}

export function hydrateDraftFromOrder(o: CstOrder): WizardDraft {
  // Every LegacyIntColumn arrives as a JSON number. Kept as a number, it failed
  // every `=== '1'` below (Individual totals came back Overall, a recurring report
  // came back one-time) and was sent back unconverted, which the save endpoint's
  // `string` rules refuse.
  const timeFrameStyleRaw = wireString(o.timeFrameStyle, '0');
  const timeFrameStyle: WizardDraft['timeFrameStyle'] =
    timeFrameStyleRaw === '1' || timeFrameStyleRaw === '2' ? timeFrameStyleRaw : '0';

  const spreadsheetSlug =
    SPREADSHEET_TYPE_SLUGS.find((s) => s.id === String(o.spreadsheetType ?? '0'))?.value ?? 'patients';
  const deliveryRaw = o.deliveryDate ?? '';
  const deliveryDatePart = deliveryRaw ? String(deliveryRaw).slice(0, 10) : '';
  const deliveryTimePart =
    deliveryRaw && String(deliveryRaw).length >= 19
      ? String(deliveryRaw).slice(11, 19)
      : '00:00:00';

  // Keep whatever legacy stored, including the delivery tokens ('das' dashboard,
  // 'pbi', 'cha', 'xls', 'eml') that the Chart-style select can't represent.
  // Rewriting them to '1' here silently downgraded a legacy dashboard order to a
  // plain table the moment it was saved from this wizard. StepDelivery renders a
  // fallback for display; the draft keeps the real value so an untouched edit
  // round-trips it unchanged.
  const outputType = String(o.outputType ?? '1');

  return {
    title: o.title ?? '',
    reportSelect: o.reportSelect ?? '',
    typesSelect1: wireString(o.typesSelect1, typesSelect1ForFamily(o.reportSelect ?? '')),
    typesSelect2: wireString(o.typesSelect2, ''),
    optionsSelect1: wireString(o.optionsSelect1, ''),
    optionsSelect2: wireString(o.optionsSelect2, ''),
    optionsSelect3: wireString(o.optionsSelect3, ''),
    optionsSelect4: wireString(o.optionsSelect4, ''),
    optionsSelect5: wireString(o.optionsSelect5, ''),
    optionsSelect6: wireString(o.optionsSelect6, ''),
    totalType: wireString(o.totalType, '2') === '1' ? '1' : '2',
    subFilterSelections: collapseCompositeSelections(
      decodeSubFilters(o.subFilters, o.filterText),
    ),

    timeFrameStyle,
    reportTimeFrame: wireString(o.reportTimeFrame, REPORT_TIME_FRAME_TODAY),
    reportDate1: toDateInput(o.reportDate1),
    reportDate2: toDateInput(o.reportDate2),
    reportIncrement: wireString(o.reportIncrement, '1'),
    reportProjection: wireString(o.reportProjection, '1'),

    audienceCoordinators: parseAudienceBucket(o.coordinators),
    audienceDoctors: parseAudienceBucket(o.doctors),
    audienceNurses: parseAudienceBucket(o.nurses),
    audienceUserlevel1: parseAudienceBucket(o.userlevel1),
    audienceUserlevel2: parseAudienceBucket(o.userlevel2),
    audienceUserlevel3: parseAudienceBucket(o.userlevel3),
    audienceUserlevel4: parseAudienceBucket(o.userlevel4),
    shareUserIds: (o.shareUserIds ?? []).map(String),
    emailAddresses: o.emailAddresses ?? '',

    outputType,
    includeSpreadsheet: String(o.spreadsheetType ?? '0') !== '0',
    spreadsheetSlug,
    spreadsheetColumns: o.spreadsheetColumns ?? '',

    recurring: wireString(o.recurring, '0') === '1' ? '1' : '0',
    deliveryDate: deliveryDatePart,
    deliveryTime: deliveryTimePart,
    deliverEvery: o.deliverEvery != null ? String(o.deliverEvery) : '1 day',
  };
}

/** A LegacyIntColumn as the string the save endpoint accepts; `fallback` when absent. */
function wireString(v: LegacyIntColumn | undefined, fallback: string): string {
  return v == null ? fallback : String(v);
}

function toDateInput(v: string | null | undefined): string {
  if (!v) return '';
  // Accept 'YYYY-MM-DD' or 'YYYY-MM-DD HH:MM:SS'
  return String(v).slice(0, 10);
}

export function buildSummary(
  d: WizardDraft,
  catalog?: ReportFilterCatalog,
): Array<{ label: string; value: string }> {
  const dateLabel =
    d.timeFrameStyle === '1'
      ? `${d.reportDate1 || '—'} → ${d.reportDate2 || '—'}`
      : REPORT_TIME_FRAMES.find((t) => t.value === d.reportTimeFrame)?.label ?? '—';
  const incrementLabel =
    REPORT_INCREMENTS.find((i) => i.value === d.reportIncrement)?.label ?? '—';
  const outputLabel = CHART_OUTPUT_TYPES.find((o) => o.value === d.outputType)?.label ?? '—';
  const audienceCount =
    Number(d.audienceCoordinators) +
    Number(d.audienceDoctors) +
    Number(d.audienceNurses) +
    Number(d.audienceUserlevel1) +
    Number(d.audienceUserlevel2) +
    Number(d.audienceUserlevel3) +
    Number(d.audienceUserlevel4);

  const typeLabel = slotDisplayValue(d, catalog?.subtype1 ?? null) ||
    slotDisplayValue(d, catalog?.subtype2 ?? null) ||
    d.typesSelect2 ||
    d.typesSelect1 ||
    '—';

  return [
    { label: 'Title', value: d.title },
    { label: 'Family', value: d.reportSelect },
    { label: 'Type', value: typeLabel },
    { label: 'Date range', value: dateLabel },
    { label: 'Increment', value: incrementLabel },
    {
      label: 'Projection',
      value: REPORT_PROJECTIONS.find((p) => p.value === d.reportProjection)?.label ?? '—',
    },
    { label: 'Totals', value: d.totalType === '1' ? 'Individual' : 'Overall' },
    {
      label: 'Audience roles',
      value: audienceCount
        ? `${audienceCount} selected`
        : d.shareUserIds.length
          ? `Owner + ${d.shareUserIds.length} shared`
          : 'Order owner only',
    },
    { label: 'Shared users', value: d.shareUserIds.length ? `${d.shareUserIds.length} users` : 'None' },
    { label: 'Email recipients', value: d.emailAddresses || '—' },
    { label: 'Output', value: outputLabel },
    {
      label: 'Schedule',
      value:
        d.recurring === '1'
          ? `Recurring (${DELIVER_EVERY_VALUES.find((v) => v.value === d.deliverEvery)?.label ?? d.deliverEvery})`
          : 'One time',
    },
  ];
}

function slotDisplayValue(draft: WizardDraft, slot: ReportOptionSlot | null): string {
  if (!slot) return '';
  const value = slotValue(draft, slot);
  if (!value) return '';
  return slot.choices.find((choice) => choice.value === value)?.label ?? value;
}
